import React, { useState, useEffect } from "react";
import { Button, message, Spin, Modal, Alert, Input } from "antd";
import { useNavigate } from "react-router-dom";
import "./ProfilePage.css";
import { logoutCurrentSession } from "./api_v2/auth";
import { getSubscriptionStatus } from "./api_v2/subscription";
import { clearToken, getToken } from "./api_v2/client";
import { deleteCurrentProfile } from "./api_v2/user";
import { useApp } from "./context/AppContext.tsx";

function ProfilePage() {
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [statusInfo, setStatusInfo] = useState(null);
  const { user, setUser, ready } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!ready) return undefined;
    if (!user?.id || !getToken()) {
      message.warning("Пользователь не найден");
      navigate("/register");
      setIsLoading(false);
      return undefined;
    }

    setUserData(user);
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const data = await getSubscriptionStatus();
        if (!cancelled && data.success) {
          setStatusInfo(data);
          setUserData((current) => ({ ...current, ...(data.user || {}) }));
        }
      } catch {
        // Authorized client handles an invalid bearer session consistently.
      }

      if (!cancelled) setIsLoading(false);
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [navigate, ready, user]);

  const handleLogout = async () => {
    try {
      await logoutCurrentSession();
    } catch {
      // Локальная сессия всё равно удаляется внутри logoutCurrentSession.
    } finally {
      setUser(null);
      message.success("Вы вышли из профиля");
      navigate("/register");
    }
  };

  const handleDeleteProfile = () => {
    // Пароль живёт только в замыкании открытого диалога. Он не попадает
    // в состояние приложения, localStorage, логи или профиль пользователя.
    let password = "";

    Modal.confirm({
      title: "Отозвать доступ и запросить удаление?",
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            Сразу после подтверждения будут отозваны все сессии, подписка,
            автоплатёж, сохранённые карты и доступ к VPN.
          </p>
          <p style={{ marginBottom: 8 }}>
            Данные профиля будут обезличены. Платёжные записи и журнал
            безопасности сохраняются в предусмотренных законом случаях.
          </p>
          <p style={{ marginBottom: 12 }}>
            Заметки, задачи и другой пользовательский контент не удаляются
            автоматически: запрос получит статус «ожидает проверки».
          </p>
          <label htmlFor="delete-profile-password">
            Текущий пароль
          </label>
          <Input.Password
            id="delete-profile-password"
            autoComplete="current-password"
            placeholder="Введите текущий пароль"
            onChange={(event) => {
              password = event.target.value;
            }}
            onPressEnter={(event) => event.preventDefault()}
            style={{ marginTop: 6 }}
          />
          <ul style={{ paddingLeft: 20, margin: "12px 0 0" }}>
            <li>Отменить отзыв доступа после подтверждения нельзя.</li>
          </ul>
        </div>
      ),
      okText: "Отозвать доступ",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onCancel: () => {
        password = "";
      },
      onOk: async () => {
        if (!password) {
          message.error("Введите текущий пароль");
          throw new Error("password_required");
        }

        try {
          const result = await deleteCurrentProfile(password);
          password = "";

          if (!result.success || !result.access_revoked) {
            throw new Error(result.message || "Не удалось отозвать доступ");
          }

          clearToken();
          setUser(null);
          Modal.success({
            title: "Доступ к аккаунту отозван",
            content:
              "Все сессии и доступы отключены немедленно. Удаление заметок, задач и другого контента ожидает проверки (pending_review).",
            okText: "Понятно",
          });
          navigate("/register");
        } catch (err) {
          const errorMessage =
            err?.response?.data?.message ||
            err?.message ||
            "Ошибка при отправке запроса на удаление";
          message.error(errorMessage);
          throw err;
        }
      },
    });
  };

  // Use v2 data if available, otherwise fall back to localStorage
  const subStatus = statusInfo?.subscription_status ?? (
    userData?.subscription_status === "active" ? "active" : "none"
  );
  const subEnd = statusInfo?.subscription?.end ?? userData?.subscription?.end_date;
  const daysLeft = subEnd
    ? Math.max(0, Math.ceil((new Date(subEnd) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;
  const subscriptionActive = subStatus === "active";

  // Build status alerts
  const statusAlerts = [];
  if (statusInfo) {
    const { auto_payment: ap, billing_violation: bv } = statusInfo;

    if (bv) {
      statusAlerts.push({
        type: "error",
        message: "Проблема с оплатой",
        description:
          "Не удалось провести автоматическое списание. VPN-доступ ограничен. " +
          "Для восстановления подключите подписку заново или обратитесь в поддержку.",
      });
    } else if (ap?.status === "retry" && ap?.fail_count > 0) {
      statusAlerts.push({
        type: "warning",
        message: `Ошибка автосписания (попытка ${ap.fail_count})`,
        description:
          "Не удаётся провести оплату по привязанной карте. " +
          "Проверьте баланс или обновите способ оплаты.",
      });
    }

    if (subStatus === "expired") {
      const endStr = subEnd
        ? new Date(subEnd).toLocaleDateString("ru-RU")
        : "";
      statusAlerts.push({
        type: "error",
        message: "Подписка истекла",
        description: `Срок действия подписки закончился${endStr ? " " + endStr : ""}. Для продления перейдите в раздел подписки.`,
      });
    } else if (subStatus === "none") {
      statusAlerts.push({
        type: "info",
        message: "Нет активной подписки",
        description: "Оформите подписку для доступа к VPN.",
      });
    } else if (subscriptionActive && daysLeft !== null) {
      if (daysLeft <= 3) {
        statusAlerts.push({
          type: "error",
          message: `Подписка истекает через ${daysLeft} ${daysLeft === 1 ? "день" : daysLeft < 5 ? "дня" : "дней"}`,
          description:
            "Скоро доступ к VPN будет отключён. Убедитесь, что карта привязана для автопродления.",
        });
      } else if (daysLeft <= 7) {
        statusAlerts.push({
          type: "warning",
          message: `Подписка истекает через ${daysLeft} дней`,
          description: "Убедитесь, что карта привязана для автопродления.",
        });
      }
    }
  }

  return (
    <div className="profile-page">
      {isLoading ? (
        <Spin size="large" />
      ) : (
        <>
          {statusAlerts.length > 0 && (
            <div className="profile-alerts">
              {statusAlerts.map((alert, i) => (
                <Alert
                  key={i}
                  type={alert.type}
                  message={alert.message}
                  description={alert.description}
                  showIcon
                />
              ))}
            </div>
          )}

          <section className="profile-card">
            <div className="profile-card__header">
              <div>
                <h2>Профиль</h2>
              </div>
            </div>
            <dl className="profile-list">
              <div>
                <dt>Имя</dt>
                <dd>{[userData?.first_name, userData?.last_name].filter(Boolean).join(" ") || userData?.name || "—"}</dd>
              </div>
              <div>
                <dt>Телефон</dt>
                <dd>{userData?.phone || "—"}</dd>
              </div>
            </dl>
          </section>

          <section className="profile-card profile-card--actions">
            <Button block onClick={() => navigate("/unlink-card")}>
              Управление оплатой
            </Button>
            <Button block onClick={handleLogout}>
              Выйти
            </Button>
            <details className="profile-danger-zone">
              <summary>Другие действия</summary>
              <Button type="text" danger onClick={handleDeleteProfile} block>
                Удалить профиль
              </Button>
            </details>
          </section>
        </>
      )}
    </div>
  );
}

export default ProfilePage;
